// Constant Response Structures
const questionStructure = `
    <exam_question>
    [Question text in Hebrew]
    </exam_question>
`;

const solutionStructure = `
    <solution>
    [Solution with detailed explanation in Hebrew]
    </solution>
`;

const matchingQuestionStructure = (matchingDefinitionsCount, matchingExamplesCount) => {
  return `
    <exam_question>
    [Question text in Hebrew, (Optional)${matchingDefinitionsCount} Definitions/Concepts in Hebrew, and ${matchingExamplesCount} examples/scenarios in Hebrew, make sure only one of them correct]
    </exam_question>`;
};

const matchingDefinitionsStructure = (matchingDefinitionsCount) => {
  return `
    <matching_definitions>[${matchingDefinitionsCount} Definitions/Concepts in Hebrew]</matching_definitions>`;
};

const matchingExamplesStructure = (matchingExamplesCount) => {
  return `
    <matching_examples>[${matchingExamplesCount} examples/scenarios in Hebrew, make sure only one of them correct]</matching_examples>`;
};

const explanationStructure = `
    <explanation>
    [Explanation of the question's relevance to the course material in Hebrew]
    </explanation>
`;

const quotationStructure = `
    <quotations>
      <quotation>
      [Direct quote from the material(including all characters and whitespaces exactly as in the material) in original language, which indicate what is the answer and why it's correct. Must be continuous, if not must be split into different quotes.]
      </quotation>

      <quotation>
      [Another direct quote from the material(including all characters and whitespaces exactly as in the material) in original language, which indicate what is the answer and why it's correct. Must be continuous, if not must be split into different quotes.]
      </quotation>
      ...
    </quotations>
`;

const changesStructure = `
    <changes>
    [Explain what changes were made related to the question, answers, matching defintions, or matching examples with respect to previous version in Hebrew.]
    </changes>
`;

const getQuestionTypeLabel = (type) => {
  switch (type) {
    case "רב-ברירה":
      return "Multiple Choice";
    case "נכון/לא נכון":
      return "True/False";
    case "התאמה":
      return "Matching";
    default:
      return type;
  }
};

const getQuestionTypesGuidelines = (formData) => ({
  "רב-ברירה":
    "- Create a multiple choice question and four optional answers (A, B, C, D)\n" +
    "- Make sure only one option is correct\n" +
    "- Create plausible but incorrect distractors for the other options\n" +
    "- Make the placements of the answers random.",

  "נכון/לא נכון":
    "- Create a clear statement that can be definitively judged as true or false",

  התאמה:
    `- Generate exactly ${formData.matchingDefinitionsCount} definitions (or terms/concepts) and exactly ${formData.matchingExamplesCount} corresponding real-life examples\n` +
    (formData.customCategoryNames?.length
      ? `- Use the following category names for the definitions list. They must appear exactly as written, in the same order:\n` +
        formData.customCategoryNames
          .map((name, i) => `  ${i + 1}. ${name}`)
          .join("\n") +
        "\n- Do not paraphrase, translate, or invent any category names.\n" +
        "- Use these names exactly and without additions.\n"
      : "") +
    "- Ensure that each example has exactly one correct and unique match from the definitions list\n" +
    "- Maintain a clear 1-to-1 correspondence between definitions and examples (no distractors, no duplicates)\n" +
    "- Ensure both lists are clearly distinct but contextually connected (e.g., term → example, concept → explanation)\n" +
    "- All content must be based on the course material and aligned with the learning objectives\n" +
    "- Format the question so that items from List A (definitions) can be matched to items from List B (examples) without ambiguity\n" +
    "- Each example have to fit to a category, But not every category must have an example.\n" +
    "- Wrap each direct quote from the material(including all characters and whitespaces exactly as in the material) in a separate <quotation>...</quotation> block.Do not combine multiple quotes into one block.\n" +
    "- Each quotation must be continuous.If not must be split into different quotes.Only use quotations that is necessary to explain the correct answer.",
});

//Update here when adding new highlight
const structuredFeedback = {
  "uniform-style": {
    title: "שמור על ניסוח דומה בין התשובות כדי לא למשוך תשומת לב לתשובה הנכונה",
    detail:
      "- ודא שכל התשובות (כולל השגויות) מנוסחות באותו סגנון, אורך ורמת שפה, כך שאף תשובה לא תבלוט או תרמוז על נכונותה.",
  },
  "valid-assessment": {
    title: "וודא שהשאלה בוחנת את הידע הרצוי",
    detail:
      "- נתח את ניסוח השאלה ובדוק שהיא מודדת בצורה מדויקת את מטרות הלמידה או את הידע/המיומנות שהחומר נועד ללמד.",
  },
  "consistent-length": {
    title: "שמור על אורך ומבנה אחיד בין כל התשובות",
    detail:
      "- ודא שכל התשובות באורך דומה ובמבנה תחבירי עקבי כדי למנוע רמזים לא מכוונים.",
  },
  "neutral-wording": {
    title: "שמור על ניסוח שאלה ניטרלי שלא יכוון את הנבחן לתשובה מסוימת",
    detail:
      "- הימנע מניסוחים חיוביים/שליליים קיצוניים או מילים שנטייתן מכוונת את הבוחר.",
  },
  "plausible-distractors": {
    title: "ודא שלכל תשובה שגויה יש אלמנט שמקשה על הזיהוי שהיא לא נכונה",
    detail:
      "- עיצוב התשובות השגויות כך שיהיו מבוססות על בלבול מושגים או טעויות נפוצות יוצר אתגר אמיתי לנבחן.",
  },
};

const generateStructuredSuggestions = (selectedIds) => {
  const result = selectedIds
    .map((id, index) => {
      const entry = structuredFeedback[id];
      if (!entry) {
        console.warn(`❗ לא נמצא הסבר עבור ID: ${id}`);
        return `(${index + 1}) [שגיאה: לא נמצא הסבר עבור ID: ${id}]`;
      }
      return `(${index + 1}) ${entry.title}\n${entry.detail}`;
    })
    .join("\n\n");
  console.log("📋 Structured Suggestions:\n\n" + result);
  return result;
};

const generateQuestionAnswerStructure = (formData) => {
  switch (formData.questionType) {
    case "רב-ברירה":
      return (
        questionStructure +
        "\n" +
        solutionStructure +
        "\n" +
        explanationStructure +
        "\n" +
        quotationStructure
      );
    case "נכון/לא נכון":
      return (
        questionStructure +
        "\n" +
        solutionStructure +
        "\n" +
        explanationStructure +
        "\n" +
        quotationStructure
      );
    case "התאמה":
      return (
        // questionStructure +
        // "\n" +
        // matchingDefinitionsStructure(formData.matchingDefinitionsCount) +
        matchingQuestionStructure(formData.matchingDefinitionsCount, formData.matchingExamplesCount) +
        // "\n" +
        // matchingExamplesStructure(formData.matchingExamplesCount) +
        "\n" +
        solutionStructure +
        "\n" +
        explanationStructure +
        "\n" +
        quotationStructure
      );
    default:
      console.error("Unsupported Question Type.");
      break;
  }
};

const generateQuestionAnswerExample = (formData) => {
  switch (formData.questionType) {
    case "נכון/לא נכון":
      return `
        Question: [Question text]\n
        Solution: The correct answer is [solution]. This is because [detailed explanation of the solution]\n
        Explanation: This question is relevant because [explanation of relevance to course material]\n
        Quotation: The exact quotations from the material that explain the correct answer [quotation].\n
      `;
    case "רב-ברירה":
      return `
        Question: [Question text]\n
        Answers: 4 answers, each on new line.\n
        Solution: The correct answer is [solution]. This is because [detailed explanation of the solution]\n
        Explanation: This question is relevant because [explanation of relevance to course material]\n
        Quotation: The exact quotations from the material that explain the correct answer [quotation].\n
      `;
    case "התאמה":
      return `
        Question: [Question text]\n
        Definitions/Concepts: Enumerate ${formData.matchingDefinitionsCount} Definitions/Concepts keep them unique no duplicates in meaning.\n
        Examples/Scenario: Enumerate ${formData.matchingExamplesCount} Examples/Scenarios, each match only one definition.\n
        Solution: The correct answer is [solution]. This is because [detailed explanation of the solution]\n
        Explanation: This question is relevant because [explanation of relevance to course material]\n
        Quotation: The exact quotations from the material that explain the correct answer [quotation].\n
      `;
    default:
      console.error("Unsupported Question Type.");
      break;
  }
};

const regeneratePromptConstructor = (
  formData,
  answers,
  matchingDefinitions,
  matchingExamples
) => {
  switch (formData.questionType) {
    case "נכון/לא נכון":
      return "";
    case "רב-ברירה":
      return `
        5. Examine the original answers for the question:
        <original_answers>
          ${answers}
        </original_answers>`;
    case "התאמה":
      return `
        5. Examine the definitions/concepts:
        <matching_definitions>
          ${matchingDefinitions}
        </matching_definitions>

        6. Examine the examples/scenarios:
        <matching_examples>
          ${matchingExamples}
        </matching_examples>
      `;
    default:
      console.error("Unsupported Question Type.");
      break;
  }
};

export const generateQuestionPrompt = (formData) => `
You are an AI assistant tasked with generating an exam question based on provided course material. Your goal is to create a high-quality question that accurately reflects the content and difficulty level specified. Follow these instructions carefully to generate the exam question:

1. Review the following course material and highlights, give the highlights higher priority as the user willing:

<course_material>
${formData.materialText}
</course_material>

<highlights>
${formData.highlightsNGuidelines}
</highlights>

${
  formData.showExampleInput
    ? `<question_example> ${formData.exampleText}</question_example>`
    : ""
}

2. Take note of the following parameters for question generation:
   - Complexity level: ${formData.complexity}
   - Question type: ${getQuestionTypeLabel(formData.questionType)}

3. Generate a ${getQuestionTypeLabel(formData.questionType)} type question:
  ${
    getQuestionTypesGuidelines(formData)[
      getQuestionTypeLabel(formData.questionType)
    ]
  }

4. For the question, provide:
   - The question text
   - For multiple choice question provide 4 answers, make sure only one of them correct, and choose randomly the placement of the answers.
   - For matching question provide ${
     formData.matchingDefinitionsCount
   } unique definitions/concepts unless the user doesn't ask for that and ${
  formData.matchingExamplesCount
} examples/scenarios that each match only one defintion/concept. 
   - An explanation of why the question is relevant to the course material
   - A solution with a detailed explanation
   - All quotation from the material(including all characters and whitespaces exactly as in the material) in the original language, that indicates what is the answer and why it's correct.
   - Provide each quotation from the material in its own <quotation> block in the original language. Use multiple <quotation> blocks if needed.
   - ${
     formData.showExampleInput
       ? "Generate similar question as provided above"
       : ""
   }

5. Write all output in Hebrew, including the question, explanation, and solution. Except for the quotations from the material, keep it in the original langauge.

${
  formData.customCategoryNames?.length
    ? `
NOTE: You MUST use the provided category names below for List A. These names are final and must not be altered.

<category_names>
${formData.customCategoryNames.map((name, i) => `${i + 1}. ${name}`).join("\n")}
</category_names>

Claude must use these names exactly, and in the same order.`
    : ""
}

6. Format your output for each question as follows:

<question>
  ${generateQuestionAnswerStructure(formData)}
</question>

Here's an example of how your output should be structured (note that this example is in English, but your actual output should be in Hebrew):

<question>
  ${generateQuestionAnswerExample(formData)}
</question>

Remember to generate question of type ${
  formData.questionType
}, all in Hebrew except the quotations in the original language, following the format specified above.`;

export const regenerateQuestionPrompt = (formData, feedback, selectedSuggestions) => `
    You are tasked with improving an exam question based on provided course material, highlights and feedback in Hebrew. Follow these steps carefully:

    1. Review the following course material:
    <course_material>
    ${formData.materialText}
    </course_material>

    2. Review the following highlights
    <highlights>
    ${formData.highlightsNGuidelines}
    </highlights>

    3. Examine the original exam question:
    <original_question>
    ${formData.question}
    </original_question>

    4. Consider the feedback provided and give it high priority in conflict situation:
    <feedback>
    ${feedback}
    הצעות שיפור שנבחרו:
    ${generateStructuredSuggestions(selectedSuggestions)}
    </feedback>

    5. Consider the question type: ${getQuestionTypeLabel(
      formData.questionType
    )}

    ${regeneratePromptConstructor(
      formData,
      formData.answers,
      formData.matchingDefinitions,
      formData.matchingExamples
    )}

    6. Analyze the course material, highlights, original question, answers, question type, feedback, matching defintions, and matching examples:
    a. Identify key concepts from the course material that should be tested
    b. Evaluate how well the original question and answers aligns with these concepts
    c. Consider how the feedback suggests the question or answers could be improved, and give the user feedback higher priorty than the structured prompt if conflict present.
    d. Make change only of what the feedback suggests, if no suggestion related to question don't change the question at all, similar to answers.
    e. if question type is matching make changes for what the feedback suggests, whether definitions or example or both. But only for way feedback refer to.
    f. Provide each quotation from the material in its own <quotation> block in the original language. Use multiple <quotation> blocks if needed.

    7. Write a better exam question, answer, matching defintions, or matching examples depends on question type and user feedback only in hebrew that:
    a. Addresses the key concepts from the course material more effectively
    b. Consider the higlights.
    c. Incorporates the suggestions from the feedback, and priorities the feedback so if there is a conflict between the structured prompt and the feedback - make changes feedback suggests.
    d. Carefully identify whether the feedback ask for improvement question or answer or both whether matching question type for improving defintion match or examples make changes only what feedback suggest.
    e. If user ask in the feedback to change the structure, set the text inside the tags to be empty.
    f. Maintains an appropriate difficulty level for the course
    g. Is clear, concise, and unambiguous
    h. If the question type is "multiple choice", create a question with the 4 number of answer options. Ensure that only one option is correct.
    i. If the question type is "true/false",  create clear statements that can be definitively judged as true or false, ensure an equal balance of true and false statements.
    j. If the question type is "matching", create a unique defintions ${
      formData.matchingDefinitionsCount
    } unless the user doesn't ask for that, and ${
  formData.matchingExamplesCount
} examples/scenarios that each match only one defintion only, make sure no ambiguous.

    8. Provide an explanation in hebrew:
    - For each question, explain what specific knowledge or skills from the course material the question is testing.
    - Discuss how the question relates to key concepts or learning objectives from the material.

    9. Provide a solution in hebrew:
    - For multiple choice questions, indicate the correct answer and explain why it is correct. Also, briefly explain why the other options are incorrect and choose randomly the placement of the answers.
    - For True/False questions, indicate the correct answer and explain why it is correct. Also, briefly explain the other statement is incorrect.
    - For Matching questions, indicate the correct matching and explain why it is correct.

    10. Provide all quotations in the original language:
    - For the answer provide all quotations from the material in the original language, which show what is the answer and why it's correct.

    11. Provide an improvement explanation in Hebrew:
    - For the new version of question or answers or matching defintions or matching examples, explain comprehensively what change made with respect to prior version and new one depend on feedback.

    12. Present your response in the following format:
    <question>
    ${generateQuestionAnswerStructure(formData) + "\n\n" + changesStructure}
    </question>

    Remember to tailor the difficulty and complexity of the questions to match the level of the course material provided. Aim to create questions that not only test recall but also understanding, application, and analysis of the material.

    Remember to generate question of type ${
      formData.questionType
    }, all in Hebrew except for the quotation in the original language, following the format specified above.

    Ensure that your improved question, answers, matching defintions, or matching examples only depend on feedback is substantively different from the original while still testing the same core concepts. Be creative in your approach, but remain focused on the course material and the goals of the exam.

    Ensure that improvements made to what feedback refer to only, question or answer or both as feedback dictates.
`;

export const getClaudeUserErrorMessage = (error) => {
  if (error.response) {
    switch (error.response.status) {
      case 401:
        return "המפתח שסיפקת אינו תקף או חסר. אנא הזן מפתח API תקף.";
      case 403:
        return "אין לך הרשאות להשתמש ב-API הזה. ודא שהמפתח תקין ועם הרשאות מתאימות.";
      case 429:
        return "חרגת ממגבלת השימוש במערכת. נסה שוב מאוחר יותר.";
      case 500:
        return "אירעה שגיאה בשרת של Claude. נסה שוב בעוד כמה רגעים.";
      default:
        return `שגיאה (${error.response.status}): נסה שוב או פנה לתמיכה.`;
    }
  } else if (error.request) {
    return "לא ניתן להתחבר לשרת. בדוק את חיבור האינטרנט שלך או נסה שוב מאוחר יותר.";
  } else {
    return `שגיאה כללית: ${error.message}`;
  }
};

export const generateQuestionByExamplePrompt = (formData) => `
You are an AI assistant tasked with generating a high-quality exam question based on the provided course material, highlights, and a question example. Your goal is to create a question that matches the example in structure, type, and difficulty, while staying strictly aligned with the course content and learning objectives.

---
**Instruction Priority Rule:**  
If there is any conflict between the instructions in this prompt and the content inside the <highlights> section, **follow the instructions and guidelines in <highlights>**. These reflect the user's specific intent and take precedence over general rules.
---

**1. Review the following content:**

<course_material>
${formData.materialText}
</course_material>

<highlights>
${formData.highlightsNGuidelines}
</highlights>

<question_example>
${formData.exampleText}
</question_example>

---

**2. Analyze the example question to determine:**
- The **difficulty level** (easy, medium, hard).
- The **question type** (e.g., multiple choice, true/false, matching, or other).
- The **learning objective or concept** it assesses.
- The **structure and style** of the example.

---

**3. Generate a new question that meets the following criteria:**
- It must be the **same type and difficulty** as the example.
- It must be **fully based on the course material and highlights**.
- **Do not invent or assume** information that is not explicitly provided.
- Ensure the question supports the **same or similar learning goal** as the example.

---

**4. Question type-specific guidelines:**

- **Multiple Choice (MCQ):**
  - Same number of answer choices as in the example (e.g., A, B, C...).
  - Only one correct answer.
  - Include plausible but incorrect distractors.
  - Randomize the answer order.

- **True/False:**
  - Write a statement that can clearly and definitively be judged as true or false.

- **Matching:**
  - List A and List B must have a **1-to-1 match**.
  - Avoid duplicates or distractors.
  - Ensure the pairs are **clearly connected** (e.g., concept → explanation, term → example).
  - It is not necessary for every item on one list to have a match if the material doesn’t support it, but every match must be valid.

- **Other types:**
  - If the example is of a different type, follow similar instructional logic and structure.

---

**5. Quotation handling:**
- Include only quotes **directly from the course material** when needed to support the answer.
- Each quote must be wrapped in its own <quotation>...</quotation> tag.
- **Preserve the original text exactly**, including all characters and whitespace.
- Do **not merge non-continuous quotes** into a single quotation block.

---

**6. Output language and formatting:**
- Write all parts of your response in **Hebrew**, except the quotations, which must remain in their **original language**.
- Format your output exactly as shown below:

<question>
  ${
  questionStructure + "\n" +
  solutionStructure + "\n" +
  explanationStructure + "\n" +
  quotationStructure
  }
</question>

Here's an example of how your output should be structured (note that this example is in English, but your actual output should be in Hebrew):

<question>
Question: [Your question text in Hebrew]

Solution: [Correct answer(s) in Hebrew]

Explanation: [Explain in Hebrew why the question is relevant to the course material and what it tests]

Solution Explanation: [Detailed answer explanation in Hebrew]

Quotation:
<quotation>[Exact text from the material]</quotation>
<quotation>[Another if needed]</quotation>
</question>

---

**7. Self-check before responding:**
- Does the question type and difficulty match the example?  
- Is it based only on the provided material and highlights?  
- Are all parts present (question, solution, explanation, quotations)?  
- Are all quotations correctly tagged and accurate?  
- Is the output in Hebrew, except for quotations?

---

**8. If the example is flawed or unclear:**
- Try to understand the likely intent.
- Generate a well-formed version of the same type and difficulty.

---

Now, generate one high-quality question according to the above instructions.
`;


export const regenerateQuestionByExamplePrompt = (
  formData,
  feedback,
  selectedSuggestions
) => `
You are an AI assistant assigned to improve an exam question written in Hebrew. Your task is to revise the question, answers, or matching structure based solely on the provided course material and feedback. Follow these steps carefully:

---

**Instruction Precedence:**
- If there's a conflict between instructions and the <highlights> section, always follow <highlights> as they represent user-specific guidelines.
- Only make changes if the feedback explicitly asks for them (e.g., question text, answers, definitions, examples). Otherwise, leave that part unchanged.

---

**1. Review the source data:**

<course_material>
${formData.materialText}
</course_material>

<highlights>
${formData.highlightsNGuidelines}
</highlights>

<original_question>
${formData.question}
</original_question>

<feedback>
${feedback}
Selected Suggestions:
${generateStructuredSuggestions(selectedSuggestions)}
</feedback>

<question_example>${formData.exampleText}</question_example>

---

**2. Analyze the original and example questions:**
- Determine question type (e.g., multiple choice, true/false, matching, or other).
- Assess difficulty level based on the example and original.
- Understand the instructional intent behind the question.

---

**3. Determine revision needs based on feedback:**
- Parse what part of the question the feedback addresses (question text, answer, match definition/example).
- Only revise what the feedback explicitly refers to.
- Do not introduce new information not present in the course material.

---

**4. Generate a revised question in Hebrew that:**
- Matches the original question type and difficulty.
- Is clearly structured and aligns with the course material and highlights.
- Incorporates only the changes explicitly requested in the feedback.

**Follow the appropriate format per question type:**

**Multiple Choice:**
- Provide options (A, B, C, ...), only one correct.
- Randomize option order.
- Include explanation for the correct answer and briefly explain why others are incorrect.

**True/False:**
- Write statements that can clearly be judged as true or false.
- Provide a balanced set (half true, half false if applicable).
- Explain correctness of each.

**Matching:**
- Ensure a 1-to-1 relationship between terms and matches.
- If feedback refers to only one side (definitions/examples), change only that side.
- Avoid distractors or duplicate mappings.

**Other types:**
- Apply logic similar to the above based on the example and feedback.

---

**5. For every revised question, provide:**

- **Question:** The revised question in Hebrew.
- **Solution:** Correct answer(s) in Hebrew.
- **Explanation:** What skill/concept is being tested, and why the question is relevant.
- **Solution Explanation:** Detailed reasoning behind the correct answer.
- **Quotations:** Relevant quotes from the course material that justify the answer, in original language.
  Use one <quotation> block per distinct quote:
  \`\`\`
  <quotation>Exact text here</quotation>
  \`\`\`

- **Improvement Explanation (in Hebrew):**  
  Explain what has been changed compared to the original version, and how the changes address the feedback.

---

**6. Output Format:**

<question>
  ${
  questionStructure + "\n" +
  solutionStructure + "\n" +
  explanationStructure + "\n" +
  quotationStructure + "\n" +
  changesStructure
  }
</question>

---

**Important Notes:**
- Do not make unnecessary changes.
- Keep output in Hebrew, except for quotations (original language).
- Ensure the revised content is substantively different from the original only where required.
- Match the complexity and structure of the original and example question.
- Provide Improvement Explanation always.

Now, generate the improved question accordingly.
`;
